import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  FlatList,
  Switch,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../components/DirectionalIcon';
import { ScreenLayout, Header, ConfirmationModal } from '../components';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useFlowPermissions } from '../hooks/useFlowPermissions';

export const FlowsListScreen: React.FC = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, activeTab, setActiveTab } = useAppContext();
  const [loading, setLoading] = useState(true);
  const [flows, setFlows] = useState<any[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingFlowId, setDeletingFlowId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingFlowId, setTogglingFlowId] = useState<string | null>(null);

  const loadFlows = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getFlows({ limit: 1000, skip: 0 });
      if (response.statusCode === 1000 && response.data?.items) {
        setFlows(response.data.items);
      } else {
        showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to load flows');
      }
    } catch (error: any) {
      console.error('Error loading flows:', error);
      showNotification('error', 'Error', error.message || 'Failed to load flows');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadFlows();
  }, [loadFlows]);

  const { canEdit, canAdmin } = useFlowPermissions();
  const handleCreate = () => navigateTo('flow-edit');
  const handleEdit = (flow: any) => navigateTo('flow-edit', flow._id);
  const handleDelete = (flowId: string) => {
    setDeletingFlowId(flowId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingFlowId) return;
    try {
      setDeleting(true);
      const response = await apiService.deleteFlow(deletingFlowId);
      if (response.statusCode === 1000) {
        showNotification('success', 'Success', 'Flow deleted successfully.');
        setShowDeleteModal(false);
        setDeletingFlowId(null);
        await loadFlows();
      } else {
        showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to delete flow');
      }
    } catch (error: any) {
      console.error('Error deleting flow:', error);
      showNotification('error', 'Error', error.message || 'Failed to delete flow');
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (flow: any) => {
    try {
      setTogglingFlowId(flow._id);
      const response = await apiService.updateFlow(flow._id, { isActive: !flow.isActive });
      if (response.statusCode === 1000) {
        setFlows((prev) => prev.map((f) => (f._id === flow._id ? { ...f, isActive: !flow.isActive } : f)));
      } else {
        showNotification('error', 'Error', response.errors?.[0]?.message || 'Failed to update flow');
      }
    } catch (error: any) {
      console.error('Error toggling flow:', error);
      showNotification('error', 'Error', error.message || 'Failed to update flow');
    } finally {
      setTogglingFlowId(null);
    }
  };

  const handleBack = () => {
    if (Platform.OS === 'web') window.history.back();
    else navigateTo('quick-reply-items');
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const renderFlow = ({ item: flow }: { item: any }) => (
    <TouchableOpacity style={styles.flowCard} onPress={() => handleEdit(flow)} activeOpacity={0.7}>
      <View style={styles.flowContent}>
        <View style={styles.flowHeader}>
          <View style={styles.flowInfo}>
            <View style={styles.flowTitleRow}>
              <Ionicons name="git-branch-outline" size={18} color={colors.primary} />
              <Text style={styles.flowName}>{flow.name}</Text>
              {!flow.isActive && (
                <View style={styles.inactiveBadge}>
                  <Text style={styles.inactiveBadgeText}>Inactive</Text>
                </View>
              )}
            </View>
            {!!flow.description && (
              <Text style={styles.flowDescription} numberOfLines={2}>{flow.description}</Text>
            )}
            <Text style={styles.flowMeta}>
              {(flow.nodes?.length || 0)} {flow.nodes?.length === 1 ? 'checker' : 'checkers'}
            </Text>
          </View>
          <View style={styles.flowActions}>
            {togglingFlowId === flow._id ? (
              <ActivityIndicator size="small" color={colors.primary} style={styles.toggleLoading} />
            ) : (
              <Switch
                value={flow.isActive}
                disabled={!canEdit}
                onValueChange={() => handleToggleActive(flow)}
                trackColor={{ false: colors.border, true: `${String(colors.primary)}80` }}
                thumbColor={flow.isActive ? colors.primary : colors.white}
              />
            )}
            {canAdmin && <TouchableOpacity
              style={styles.actionButton}
              onPress={(e) => {
                e.stopPropagation();
                handleDelete(flow._id);
              }}
              activeOpacity={0.7}
            >
              <Ionicons name="trash-outline" size={18} color={colors.error} />
            </TouchableOpacity>}
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );

  if (loading) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        rightPaneContent={null}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.loadingText}>Loading flows...</Text>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent={null}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <TouchableOpacity style={styles.backButton} onPress={handleBack}>
              <DirectionalIcon name="arrow-back" size={24} color={colors.text.primary} />
            </TouchableOpacity>
            <View style={styles.titleContainer}>
              <Text style={styles.title}>Flows</Text>
              <Text style={styles.subtitle}>{flows.length} {flows.length === 1 ? 'flow' : 'flows'}</Text>
            </View>
            <TouchableOpacity accessibilityLabel="Flow runs" style={[styles.addButton, { backgroundColor: colors.background.surface, borderWidth: 1, borderColor: colors.border, marginRight: 8 }]} onPress={() => navigateTo('dashboard-flows-all')} activeOpacity={0.8}>
              <Ionicons name="pulse-outline" size={22} color={colors.primary as string} />
            </TouchableOpacity>
            {canEdit && <TouchableOpacity style={styles.addButton} onPress={handleCreate} activeOpacity={0.8}>
              <Ionicons name="add" size={24} color={colors.white} />
            </TouchableOpacity>}
          </View>
        </View>

        {flows.length === 0 ? (
          <View style={styles.emptyContainer}>
            <Ionicons name="git-branch-outline" size={64} color={colors.textLight} />
            <Text style={styles.emptyText}>No flows yet</Text>
            <Text style={styles.emptySubtext}>
              Build a reusable decision tree with multiple checkers, then attach it to reply items, datasets, or channel settings.
            </Text>
            {canEdit && <TouchableOpacity style={styles.emptyButton} onPress={handleCreate} activeOpacity={0.7}>
              <Ionicons name="add-circle-outline" size={20} color={colors.primary} />
              <Text style={styles.emptyButtonText}>Create Flow</Text>
            </TouchableOpacity>}
          </View>
        ) : (
          <FlatList
            data={flows}
            renderItem={renderFlow}
            keyExtractor={(flow, index) => flow._id || `flow-${index}`}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={false}
          />
        )}

        <ConfirmationModal
          visible={showDeleteModal}
          title="Delete Flow"
          message="Are you sure you want to delete this flow? Any attachments using it will stop working. This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={confirmDelete}
          onCancel={() => {
            setShowDeleteModal(false);
            setDeletingFlowId(null);
          }}
          confirmButtonColor={colors.error}
          loading={deleting}
        />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.lightGray },
  header: { padding: spacing.lg, backgroundColor: colors.background.surface, borderBottomWidth: 1, borderBottomColor: '#E5E7EB' },
  headerRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  backButton: { padding: spacing.xs, marginRight: spacing.md },
  titleContainer: { flex: 1 },
  title: { fontSize: typography.fontSize.xl, fontFamily: typography.fontFamily.bold, color: colors.textDark, marginBottom: spacing.xs },
  subtitle: { fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.regular, color: colors.text.secondary },
  addButton: { width: 40, height: 40, borderRadius: 20, backgroundColor: colors.primary, justifyContent: 'center', alignItems: 'center', ...designTokens.shadows.sm },
  listContent: { padding: spacing.md, paddingBottom: spacing.xl },
  flowCard: { backgroundColor: colors.background.surface, borderRadius: designTokens.borderRadius.lg, padding: spacing.md, marginBottom: spacing.md, ...designTokens.shadows.sm },
  flowContent: { flex: 1 },
  flowHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  flowInfo: { flex: 1, marginRight: spacing.md },
  flowTitleRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, marginBottom: spacing.xs / 2 },
  flowName: { fontSize: typography.fontSize.base, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold },
  inactiveBadge: { backgroundColor: '#FEF3C7', paddingHorizontal: spacing.xs, paddingVertical: 2, borderRadius: designTokens.borderRadius.sm },
  inactiveBadgeText: { fontSize: typography.fontSize.xs, color: '#D97706', fontFamily: typography.fontFamily.medium },
  flowDescription: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.primary, marginBottom: spacing.xs / 2 },
  flowMeta: { fontSize: typography.fontSize.sm, color: colors.textLight, fontFamily: typography.fontFamily.primary },
  flowActions: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  toggleLoading: { width: 51 },
  actionButton: { width: 36, height: 36, borderRadius: 18, backgroundColor: colors.lightGray, justifyContent: 'center', alignItems: 'center' },
  emptyContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl * 2 },
  emptyText: { fontSize: typography.fontSize.xl, fontWeight: '600', color: colors.textDark, fontFamily: typography.fontFamily.semibold, marginTop: spacing.lg, marginBottom: spacing.xs },
  emptySubtext: { fontSize: typography.fontSize.base, color: colors.textLight, fontFamily: typography.fontFamily.primary, textAlign: 'center', marginBottom: spacing.xl },
  emptyButton: { flexDirection: 'row', alignItems: 'center', backgroundColor: colors.primary + '15', paddingHorizontal: spacing.md, paddingVertical: spacing.sm, borderRadius: designTokens.borderRadius.md, gap: spacing.xs },
  emptyButtonText: { fontSize: typography.fontSize.base, fontWeight: '600', color: colors.primary, fontFamily: typography.fontFamily.semibold },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  loadingText: { marginTop: spacing.md, fontSize: typography.fontSize.base, color: colors.textLight, fontFamily: typography.fontFamily.primary },
});
